import { Box, LinearProgress } from "@mui/material";
import { Ref, forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";

import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { ChatContext, ChatContextValue } from "@/common/contexts/chat";
import { ChatExtraContext, ChatHelper } from "@/common/helpers/entity/chat";
import { useTenantProfileV2 } from "@/common/hooks/entity/tenant/useTenantProfileV2";
import { useMemoWithDeepCompare } from "@/common/hooks/memo/useMemoWithDeepCompare";
import { useAppSelector, useAppThunkDispatch } from "@/common/hooks/redux";
import { ChatDto, ChatType, GeneralScopeDto } from "@/core/api/generated/v1";
import { joinChat, leaveChat } from "@/store/communication/chatParticipantsSlice";
import { acknowledgeChat, reopenChat, resolveChat } from "@/store/communication/chatsSlice";
import {
  selectChat,
  selectChatCurrentParticipant,
  selectIsChatClosing,
  selectIsChatOpening,
} from "@/store/communication/selectors";
import { ResponsiveStyleValueCustom } from "@/theme/types";

import ChatSkeleton from "../../Skeleton/ChatSkeleton";
import { ChatHistoryProps } from "../ChatHistory/ChatHistory";
import { ChatMessageInputFeaturesProps } from "../ChatMessage/Input/ChatMessageInput";
import ChatDisplay, { ChatDisplayHandle, ChatDisplayProps, ChatSize } from "./ChatDisplay";

export interface OwnProps {
  chatType: ChatType;
  chatId: string | undefined | null;
  scope: GeneralScopeDto | undefined | null;
  extraContext?: ChatExtraContext;

  allowPin?: boolean;
  allowClose?: boolean;
  allowLeave?: boolean;
  allowAttachments?: boolean;
  allowOpenInNewTab?: boolean;
  allowEdit?: boolean;
  allowParticipantManagement?: boolean;
  allowAcknowledgement?: boolean;
  allowUpdateTags?: boolean;
  size?: ChatSize;
  disabled?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
  maxHeight?: string | number;
  chatHistoryHeight?: ResponsiveStyleValueCustom<number>;
  chatHistoryProps?: ChatHistoryProps;
  chatInputFeatures?: ChatMessageInputFeaturesProps;
  displayProps?: ChatDisplayProps;
  onPin?: (chat: ChatDto) => void;
  onClose?: (chat: ChatDto) => void;
  onSendMessage?: () => void;
}

export type ChatProps = OwnProps;

interface Props extends OwnProps {
  chat?: ChatDto;
}

export default forwardRef(function Chat(
  {
    chat,
    chatId,
    chatType,
    scope,
    extraContext,

    allowPin,
    allowClose,
    allowLeave,
    allowAttachments,
    allowOpenInNewTab,
    allowEdit,
    allowParticipantManagement,
    allowAcknowledgement,
    allowUpdateTags,
    size,
    disabled,
    fullWidth,
    fullHeight,
    maxHeight,
    chatHistoryHeight,
    chatHistoryProps,
    chatInputFeatures,
    displayProps,
    onPin,
    onClose,
    onSendMessage,
  }: Props,
  ref: Ref<ChatDisplayHandle>,
) {
  const thunkDispatch = useAppThunkDispatch();

  const tenantProfile = useTenantProfileV2();
  const currentChat = useAppSelector(
    (state) => chat || selectChat(state, { chatId: chatId, scope: scope }),
  );
  const currentChatId = chatId || currentChat?.id || "";
  const isScopedChat = !!scope;
  const isValidScope = isScopedChat ? !!scope?.identifier : true;

  const isChatOpening = useAppSelector((state) => selectIsChatOpening(state, currentChat?.id));
  const paginatedParticipants = useAppSelector(
    (state) =>
      state.communication.chatParticipants.paginatedChatParticipantMap[currentChatId] || undefined,
  );
  const hasPinnedItems = useAppSelector(
    (state) =>
      state.communication.chatHistory.pinnedChatHistoryItems[currentChatId]?.items?.length !== 0 ||
      false,
  );
  const isChatClosing = useAppSelector((state) => selectIsChatClosing(state, currentChat?.id));
  const currentParticipant = useAppSelector((state) =>
    selectChatCurrentParticipant(state, currentChat?.id),
  );

  const chatRef = useRef<ChatDisplayHandle>(null);
  useImperativeHandle(ref, () => chatRef.current as ChatDisplayHandle, [chatRef.current]);

  const participants = paginatedParticipants?.items;

  const extraContextComputed = useMemoWithDeepCompare(() => extraContext, [extraContext]);

  const chatPerms = useMemo(
    () =>
      ChatHelper.getPermissions({
        chat: currentChat,
        participants: participants,
        currentParticipant: currentParticipant,
      }),
    [currentChat, participants, currentParticipant],
  );

  // create and sync the context
  const chatContextValue = useMemo(() => new ChatContextValue(), []);
  useEffect(() => {
    chatContextValue.setChatData({
      tenantProfile: tenantProfile.profile,
      chatPerms: chatPerms,
      chat: currentChat,
      participants: participants,
      currentParticipant: currentParticipant,
      extra: extraContextComputed,
    });
  }, [
    chatContextValue,
    tenantProfile.profile,
    chatPerms,
    currentChat,
    participants,
    currentParticipant,
    extraContextComputed,
  ]);

  useBreadcrumbReplacements({
    isEnabled: displayProps?.breadcrumbs,
    waitTimeout: 10_000,
    idBreadcrumb:
      (currentChat && {
        idValue: currentChat.id!,
        newTitle: `Chat: "${currentChat.name || currentChat.localNumber || ""}"`,
      }) ||
      undefined,
  });

  if (!isValidScope) {
    return <Box>Invalid chat scope specified!</Box>;
  }

  if (isChatOpening) {
    return <ChatSkeleton />;
  }

  return (
    (currentChat && (
      <ChatContext.Provider value={chatContextValue}>
        {isChatClosing && <LinearProgress sx={{ my: 1 }} />}

        <ChatDisplay
          ref={chatRef}
          disabled={disabled}
          chatPerms={chatPerms}
          chat={currentChat}
          participants={participants}
          hasPinnedItems={hasPinnedItems}
          resolveChat={(x) => thunkDispatch(resolveChat(x))}
          reopenChat={(x) => thunkDispatch(reopenChat(x))}
          acknowledgeChat={(x) => thunkDispatch(acknowledgeChat(x))}
          joinChat={(x) => thunkDispatch(joinChat(x))}
          leaveChat={(x) => thunkDispatch(leaveChat(x))}
          allowPin={allowPin}
          allowClose={allowClose}
          allowLeave={allowLeave}
          allowAttachments={allowAttachments}
          allowOpenInNewTab={allowOpenInNewTab}
          allowEdit={allowEdit}
          allowParticipantManagement={allowParticipantManagement}
          allowAcknowledgement={allowAcknowledgement}
          allowUpdateTags={allowUpdateTags}
          size={size}
          fullWidth={fullWidth}
          fullHeight={fullHeight}
          maxHeight={maxHeight}
          chatHistoryHeight={chatHistoryHeight}
          chatHistoryProps={chatHistoryProps}
          chatInputFeatures={chatInputFeatures}
          displayProps={displayProps}
          onPin={onPin}
          onClose={onClose}
          onSendMessage={onSendMessage}
        />
      </ChatContext.Provider>
    )) ||
    null
  );
});
